import {Component, computed, effect, inject, input, output, signal, untracked} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {InputText} from 'primeng/inputtext';
import {CompanyInfoResponse, CompanyInfoSection} from '@frontend/shared-api';
import {DirectusSelect} from '@frontend/directus';
import {DirectusSelectComponent} from '../../../../../../../shared/components/inputs/directus-select/directus-select.component';
import {ProgressBarComponent} from '../../../../../../../shared/components/progress-bar/progress-bar.component';
import {ProjectStepSaveButtonComponent} from '../../../../../../../shared/project-step-save-button/project-step-save-button.component';
import {
  completedSectionCount,
  fteExceedsHeadcount,
  isSectionDirty,
  pickSectionValues,
  sectionByName,
  sectionStatus,
  unitKpis,
} from '../company-info.calc';
import {FigureInputDirective} from '../figure-input/figure-input.directive';
import {SectionStatusChipComponent} from '../section-status-chip/section-status-chip.component';
import {Step11CompanyInformationService} from '../step-1-1.service';
import {
  CompanyInfoValues,
  EMPTY_COMPANY_INFO_VALUES,
  GERMANY_COUNTRY_CODE,
  Office,
  SECTION_DEFS,
} from '../step-1-1-company-information.types';
import {WorkforceKpiCardComponent} from '../workforce-kpi-card/workforce-kpi-card.component';
import {SectionView} from './company-unit-form.types';

const SAVED_INDICATOR_DURATION_MS = 2500;

type CompanyInfoForm = FormGroup<{
  [Field in keyof CompanyInfoValues]: FormControl<CompanyInfoValues[Field]>;
}>;

function buildCompanyInfoForm(): CompanyInfoForm {
  return new FormGroup({
    industryId: new FormControl<string | null>(null),
    tariffId: new FormControl<string | null>(null),
    salariedHeadcountFullTime: new FormControl<number | null>(null),
    salariedHeadcountPartTime: new FormControl<number | null>(null),
    salariedFte: new FormControl<number | null>(null),
    industrialHeadcountFullTime: new FormControl<number | null>(null),
    industrialHeadcountPartTime: new FormControl<number | null>(null),
    industrialFte: new FormControl<number | null>(null),
    hrHeadcountFullTime: new FormControl<number | null>(null),
    hrHeadcountPartTime: new FormControl<number | null>(null),
    hrFte: new FormControl<number | null>(null),
    contingentHeadcountFullTime: new FormControl<number | null>(null),
    contingentHeadcountPartTime: new FormControl<number | null>(null),
    contingentFte: new FormControl<number | null>(null),
    pensioners: new FormControl<number | null>(null),
    inactiveEmployees: new FormControl<number | null>(null),
  });
}

function isCompanyInfoSection(value: unknown): value is CompanyInfoSection {
  return typeof value === 'string' && SECTION_DEFS.some((section) => section.name === value);
}

@Component({
  selector: 'app-company-unit-form',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    InputText,
    DirectusSelectComponent,
    ProgressBarComponent,
    ProjectStepSaveButtonComponent,
    SectionStatusChipComponent,
    WorkforceKpiCardComponent,
    FigureInputDirective,
  ],
  templateUrl: './company-unit-form.component.html',
  styleUrl: './company-unit-form.component.scss',
})
export class CompanyUnitFormComponent {
  private readonly step11Service = inject(Step11CompanyInformationService);

  readonly projectId = input.required<number>();
  readonly office = input.required<Office>();
  readonly savedValues = input.required<CompanyInfoValues>();
  readonly industryDropdown = input<DirectusSelect | null>(null);
  readonly tariffDropdown = input<DirectusSelect | null>(null);
  readonly locked = input(false);

  readonly valuesChange = output<CompanyInfoValues>();
  readonly saved = output<CompanyInfoResponse>();

  protected readonly form = buildCompanyInfoForm();
  protected readonly sectionCount = SECTION_DEFS.length;
  protected readonly industryDropdownKey = 'industry';
  protected readonly tariffDropdownKey = 'tariff';

  private readonly currentValues = signal<CompanyInfoValues>(EMPTY_COMPANY_INFO_VALUES);
  private readonly baseline = signal<CompanyInfoValues>(EMPTY_COMPANY_INFO_VALUES);
  private readonly savingSection = signal<CompanyInfoSection | null>(null);
  private readonly justSavedSection = signal<CompanyInfoSection | null>(null);
  protected readonly openPanels = signal<CompanyInfoSection[]>([]);

  protected readonly tariffRequired = computed(
    () => this.office().countryCode === GERMANY_COUNTRY_CODE,
  );
  protected readonly selectedIndustry = computed(() => this.currentValues().industryId);
  protected readonly selectedTariff = computed(() => this.currentValues().tariffId);
  protected readonly tariffSelectDisabled = computed(
    () => this.locked() || !this.selectedIndustry(),
  );
  protected readonly kpis = computed(() => unitKpis(this.currentValues()));
  protected readonly completedSections = computed(() =>
    completedSectionCount(this.currentValues(), this.tariffRequired()),
  );
  protected readonly sectionViews = computed<SectionView[]>(() => this.buildSectionViews());

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.publishValues());
    effect(() => this.applySavedValues());
    effect(() => this.applyLockState());
  }

  onIndustrySelected(value: string): void {
    this.form.controls.industryId.setValue(value);
    if (this.tariffRequired()) {
      this.form.controls.tariffId.setValue(null);
    }
  }

  onTariffSelected(value: string): void {
    this.form.controls.tariffId.setValue(value);
  }

  onOpenPanelsChange(value: unknown): void {
    this.openPanels.set(Array.isArray(value) ? value.filter(isCompanyInfoSection) : []);
  }

  async saveSection(section: CompanyInfoSection): Promise<void> {
    const sectionDef = sectionByName(section);
    if (!sectionDef) {
      return;
    }
    const sectionValues = pickSectionValues(this.form.getRawValue(), sectionDef);
    this.savingSection.set(section);
    try {
      const response = await this.step11Service.saveSection(
        this.projectId(),
        this.office().id,
        section,
        sectionValues,
      );
      this.baseline.update((baseline) => ({...baseline, ...sectionValues}));
      this.flashSavedIndicator(section);
      this.openPanels.update((panels) => panels.filter((panel) => panel !== section));
      this.saved.emit(response);
    } finally {
      this.savingSection.set(null);
    }
  }

  private applySavedValues(): void {
    const savedValues = this.savedValues();
    untracked(() => {
      this.baseline.set(savedValues);
      this.form.patchValue(savedValues);
    });
  }

  private applyLockState(): void {
    const locked = this.locked();
    untracked(() => {
      if (locked) {
        this.form.disable({emitEvent: false});
      } else {
        this.form.enable({emitEvent: false});
      }
    });
  }

  private publishValues(): void {
    const values = this.form.getRawValue();
    this.currentValues.set(values);
    this.valuesChange.emit(values);
  }

  private flashSavedIndicator(section: CompanyInfoSection): void {
    this.justSavedSection.set(section);
    setTimeout(
      () => this.justSavedSection.update((current) => (current === section ? null : current)),
      SAVED_INDICATOR_DURATION_MS,
    );
  }

  private buildSectionViews(): SectionView[] {
    const values = this.currentValues();
    const baseline = this.baseline();
    const tariffRequired = this.tariffRequired();
    const savingSection = this.savingSection();
    const justSavedSection = this.justSavedSection();
    const locked = this.locked();

    return SECTION_DEFS.map((def) => ({
      def,
      status: sectionStatus(values, def, tariffRequired),
      saving: savingSection === def.name,
      justSaved: justSavedSection === def.name,
      showSave: !locked && isSectionDirty(values, baseline, def),
      categories: def.categories.map((category) => ({
        def: category,
        fteWarning: fteExceedsHeadcount(values, category),
      })),
    }));
  }
}
